import { i18n } from '@boot/i18n.js';
import { convertToUnitSystem } from '@models/products.js';
import { getLocaleFormat } from '@models/timeformats.js';
import { getAsset } from '@store/facilities/getters.js';
import { checkOperatingTimeExceedsResolution } from '@utils/sentryUtils';
import DateGenerator from '@utils/dateGenerator.js';
import { roundTo } from '@utils/numberFormatters.js';
import { getDateLabel } from '@utils/time.js';
import {
    addValueToTotal,
    addValueToUnitTotal,
    getPeriodKey,
    calculatePerformance,
    calculateAvailability,
    calculateQuality,
    updateProductData,
    calculateNetProduction,
    calculateOeeMetrics
} from './trendsReportUtils';

export default class TrendsReportBuilder {
    constructor({
        records,
        startDateUTC,
        endDateUTC,
        timezone,
        resolution,
        userLocale,
        assetIdToName,
        facilityName
    }) {
        this.records = records;
        this.startDate = startDateUTC;
        this.endDate = endDateUTC;
        this.resolution = resolution;
        this.userLocale = userLocale;
        this.timezone = timezone;
        this.assetIdToName = assetIdToName;
        this.facilityName = facilityName;

        // periods will be used as the table columns and chart labels
        this.periods = [];
        this.initReportPeriods();

        /**
         * aggregatedData contains a list of assets and their aggregated data based on the period totals.
         * Used for the OEE Metrics and total production views
         * @example
         * {
         *    5: { // <-- asset_id
         *        '2023-12-18T00:00:00': { // <-- period
         *            "oee": 0.9841443411700384,
         *            "availability": 1,
         *            "performance": 0.9841443411700384,
         *            "quality": 1,
         *            "gross_product_count": 75,
         *            "gross_quantity": 1425,
         *            "net_product_count": 75,
         *            "net_quantity": 1425,
         *            "unit_measure": "kg"
         *        },
         *    }
         * }
         */
        this.aggregatedData = {};

        /**
         * periodTotals contain a list of assets and their raw period totals.
         * Used to compute for the OEE Metrics and total production.
         *
         * @example
         * {
         *   5: { // <-- asset_id
         *      '2023-12-18T00:00:00': { // <-- period
         *          "total_production": 75,
         *          "total_production_quantity": 1425,
         *          "production_quantity_by_unit": {
         *             "kg": 1425
         *          },
         *          "total_reject_production": 0,
         *          "total_reject_production_quantity": 0,
         *          "reject_production_quantity_by_unit": {
         *            "kg": 0
         *          },
         *          "total_expected_production": 76.20833333333333,
         *          "total_operating_time": 60.96666666666667,
         *          "total_available_time": 60.96666666666667,
         *      }
         *   }
         * }
         */
        this.periodTotals = {};

        /**
         * productsData contains the raw data for each asset and their products.
         * Used for more detailed views like tables and exported files.
         * @example
         * {
         *    5: { // <-- asset_id
         *        1: { // <-- product_id
         *            product_name: 'Product 1',
         *            unit_measure: 'kg',
         *            '2023-12-18T00:00:00': { <-- period
         *                "oee": 0.984144341170038,
         *                "availability": 1,
         *                "performance": 0.984144341170038,
         *                "quality": 1,
         *                "production": 75,
         *                "reject_production": 0,
         *                "unit_packing": 19,
         *                "unit_measure": "kg",
         *                "running_duration": 3642,
         *                "availability_downtime_duration": 0,
         *                "performance_downtime_duration": 16,
         *                "expected_run_rate": 1.25
         *            },
         *           '2023-12-19T00:00:00': {...} <-- period
         *        }
         *    }
         * }
         */
        this.productsData = {};

        /**
         * assetTotals contains the total values for each asset.
         * Used to store totals for the OEE Metrics and production.
         * @example
         * {
         *   5: { // <-- asset_id
         *     "total_production": 75,
         *     "total_production_quantity": 1425,
         *     "total_net_production": 75,
         *     "total_net_production_quantity": 1425,
         *     "run_rates": [1.25, 1.25, ...], <-- run rates for each product
         *      oee: 0.9841443411700384,
         *      availability: 1,
         *      performance: 0.9841443411700384,
         *      quality: 1
         *  }
         * }
         */
        this.assetTotals = {};
    }

    /**
     * Build the report data that is easier to process for the table and chart views.
     */
    buildReport() {
        // process each record and group them to the productsData and periodTotals
        const initializedProducts = new Set();
        for (const record of this.records) {
            updateProductData(
                this.productsData,
                record,
                this.periods,
                initializedProducts
            );
            this.addRecordValuesToPeriodTotal(record);
        }
        // Use the total values of the periods to compute for the OEE Metrics and total production
        this.aggregatedData = this.getAggregatedData();
    }

    /*
     * In this function, we accumulate the raw total values for each period.
     * These values are used to compute for the OEE Metrics and total production.
     * @param {Object} record - trend data
     */
    addRecordValuesToPeriodTotal(record) {
        const { asset_id, period } = record;
        // Initialize the period totals if not yet initialized
        if (!this.periodTotals[asset_id]) {
            this.periodTotals[asset_id] = {
                ...this.periods.reduce((acc, period) => {
                    acc[period] = {
                        total_production: null,
                        total_production_quantity: null,
                        production_quantity_by_unit: {},
                        total_reject_production: null,
                        total_reject_production_quantity: null,
                        reject_production_quantity_by_unit: {},
                        total_expected_production: null,
                        total_operating_time: null,
                        total_available_time: null,
                        unit_measure: null
                    };
                    return acc;
                }, {})
            };
        }
        const periodKey = getPeriodKey(period);
        const periodTotal = this.periodTotals[asset_id][periodKey];

        // Check if periodTotal is undefined and handle gracefully
        if (!periodTotal) {
            console.warn(
                `Period total not found for asset ${asset_id}, period ${periodKey}. This record will be skipped.`
            );
            return; // Skip this record as it's outside the expected period range
        }

        // Destructuring properties from the 'record' object for better readability
        const {
            production,
            reject_production,
            expected_run_rate,
            running_duration,
            performance_downtime_duration,
            availability_downtime_duration,
            unit_packing,
            unit_measure
        } = record;

        // Add the record values to the period totals
        periodTotal.total_production = addValueToTotal(
            production,
            periodTotal.total_production
        );

        if (production !== null && unit_packing !== null) {
            const production_quantity = production * unit_packing;
            periodTotal.total_production_quantity = addValueToTotal(
                production_quantity,
                periodTotal.total_production_quantity
            );
            periodTotal.production_quantity_by_unit = addValueToUnitTotal(
                production_quantity,
                unit_measure,
                periodTotal.production_quantity_by_unit
            );
        }
        periodTotal.total_reject_production = addValueToTotal(
            reject_production,
            periodTotal.total_reject_production
        );
        if (reject_production !== null && unit_packing !== null) {
            const reject_production_quantity = reject_production * unit_packing;
            periodTotal.total_reject_production_quantity = addValueToTotal(
                reject_production_quantity,
                periodTotal.total_reject_production_quantity
            );
            periodTotal.reject_production_quantity_by_unit =
                addValueToUnitTotal(
                    reject_production_quantity,
                    unit_measure,
                    periodTotal.reject_production_quantity_by_unit
                );
        }

        const expected_production =
            (expected_run_rate *
                (running_duration + performance_downtime_duration)) /
            60;
        periodTotal.total_expected_production = addValueToTotal(
            expected_production,
            periodTotal.total_expected_production
        );

        const operating_time =
            (running_duration + performance_downtime_duration) / 60;
        periodTotal.total_operating_time = addValueToTotal(
            operating_time,
            periodTotal.total_operating_time
        );

        const available_time =
            (running_duration +
                performance_downtime_duration +
                availability_downtime_duration) /
            60;
        periodTotal.total_available_time = addValueToTotal(
            available_time,
            periodTotal.total_available_time
        );

        // Check if total operating time exceeds the resolution period
        const assetName = this.assetIdToName[asset_id] || `Asset ${asset_id}`;
        checkOperatingTimeExceedsResolution({
            asset_id,
            asset_name: assetName,
            period: periodKey,
            total_operating_time: periodTotal.total_operating_time,
            resolution: this.resolution,
            facility_name: this.facilityName
        });
    }

    /**
     * Get the aggregated data for each asset by processing the period totals.
     * @return {Object} - Aggregated data for each asset
     */
    getAggregatedData() {
        const aggregatedData = {};
        for (const [asset_id, assetPeriodData] of Object.entries(
            this.periodTotals
        )) {
            const assetTotals = {};
            for (const [period, periodTotal] of Object.entries(
                assetPeriodData
            )) {
                const {
                    total_production,
                    total_production_quantity,
                    production_quantity_by_unit,
                    total_reject_production,
                    total_reject_production_quantity,
                    reject_production_quantity_by_unit,
                    total_expected_production,
                    total_operating_time,
                    total_available_time,
                    unit_measure
                } = periodTotal;
                // calculate the TOTAL net production and net production quantity.
                const total_net_production =
                    total_production !== null &&
                    total_reject_production !== null
                        ? total_production - total_reject_production
                        : null;
                const total_net_production_quantity =
                    total_production_quantity !== null &&
                    total_reject_production_quantity !== null
                        ? total_production_quantity -
                          total_reject_production_quantity
                        : null;
                const net_production_quantity_by_unit = calculateNetProduction(
                    production_quantity_by_unit,
                    reject_production_quantity_by_unit
                );
                const { availability, performance, quality, oee } =
                    calculateOeeMetrics(
                        total_operating_time,
                        total_available_time,
                        total_production,
                        total_expected_production,
                        total_net_production
                    );
                assetTotals[period] = {
                    oee,
                    availability,
                    performance,
                    quality,
                    gross_product_count: total_production,
                    gross_quantity: total_production_quantity,
                    gross_quantity_by_unit: production_quantity_by_unit,
                    net_product_count: total_net_production,
                    net_quantity: total_net_production_quantity,
                    net_quantity_by_unit: net_production_quantity_by_unit,
                    unit_measure
                };
            }
            aggregatedData[asset_id] = assetTotals;
        }
        return aggregatedData;
    }

    /**
     * Get the table data for each asset. This contains the columns and rows to be used by Quasar's QTable.
     * @return {Array} - Array of objects containing the columns and rows
     */
    getTableData() {
        const tableData = [];

        // for each asset in the report data, generate the columns and rows
        for (const asset_id of Object.keys(this.productsData)) {
            const columns = this.generateTableColumns();

            const assetAggregateData = this.aggregatedData[asset_id];
            const assetPeriodTotal = this.periodTotals[asset_id];
            const oeeMetricRow = this.generateTableOEEMetricRow(
                assetAggregateData,
                assetPeriodTotal
            );
            const totalProductionRow =
                this.generateTableTotalProductionRow(assetAggregateData);

            const productsData = this.productsData[asset_id];
            const productRows = this.generateTableProductRows(productsData);

            const tableDataPerAsset = {
                asset_id: parseInt(asset_id),
                columns: columns,
                rows: [oeeMetricRow, totalProductionRow, ...productRows]
            };
            tableData.push(tableDataPerAsset);

            // Store the asset totals for this asset
            this.updateAssetTotals(
                asset_id,
                totalProductionRow,
                oeeMetricRow,
                productRows
            );
        }

        // Alphabetically sort the table data by asset name
        tableData.sort((a, b) => {
            const assetNameA = this.assetIdToName[a.asset_id];
            const assetNameB = this.assetIdToName[b.asset_id];
            return assetNameA.localeCompare(assetNameB);
        });

        return tableData;
    }

    /**
     * Get the graph data for an asset.
     * This contains the periods and the OEE Metrics data for the chart.
     * @param {Number} asset_id - Asset id
     * @returns {Object} - Graph data for the chart
     */
    getGraphData(asset_id) {
        const assetAggregateData = this.aggregatedData[asset_id];

        // Initialize the graphData object for the chart
        const graphData = {
            periods: new Set(),
            oee: [],
            availability: [],
            performance: [],
            quality: []
        };

        // tally the total oee metrics for each period
        for (const [periodKey, periodData] of Object.entries(
            assetAggregateData
        )) {
            if (periodData !== null) {
                // add the period data to the graphData object
                graphData.periods.add(periodKey);
                graphData.oee.push(periodData.oee);
                graphData.availability.push(periodData.availability);
                graphData.performance.push(periodData.performance);
                graphData.quality.push(periodData.quality);
            }
        }
        return graphData;
    }

    /**
     * Get the file data for an asset. This contains the date range and the rows to be written to the file.
     * @param {Number} asset_id - Asset id
     * @param {String} unitSystem - Unit system to convert the production quantity
     * @return {Object} - Object containing the date range and the rows formatted for the file exporter
     *
     * @example
     * {
     *  start_date: '2023-01-01T00:00:00',
     *  end_end: '2023-01-04T00:00:00',
     *  rows: [
     *      [ <column row>, ... ],
     *      [ '2024-01-01T00:00:00', 'Vancouver', 'Filler 1', 'Product A', ... ],
     *      [ '2024-01-01T00:00:00', 'Vancouver', 'Filler 1', 'Product B', ... ]
     *  ]
     */
    getFileRowsData(asset_id, unitSystem) {
        const rows = [];

        // for each product in each asset, generate the rows
        for (const productData of Object.values(this.productsData[asset_id])) {
            const productName = productData.product_name;
            const unitMeasure = productData.unit_measure;

            // convert the unit measure to the unit system
            const { unit: converted_unit_measure } = convertToUnitSystem(
                0, // dummy value since we only need the unit
                unitMeasure,
                unitSystem
            );

            // Only iterate periods that are not metadata keys
            for (const [period, periodData] of Object.entries(productData)) {
                if (period === 'product_name' || period === 'unit_measure')
                    continue;
                // Do not include the period if there is no data
                if (periodData !== null) {
                    // convert the unit packing to the unit system
                    const { value: converted_unit_packing } =
                        convertToUnitSystem(
                            periodData.unit_packing,
                            unitMeasure,
                            unitSystem
                        );

                    const date = getLocaleFormat(period, this.userLocale);

                    // Calculate the gross production count/quantity
                    const gross_product_count = periodData.production;
                    let gross_quantity = null;
                    if (
                        periodData.production !== null &&
                        converted_unit_packing !== null
                    ) {
                        gross_quantity = roundTo(
                            periodData.production * converted_unit_packing,
                            3
                        );
                    }

                    // Calculate the net production count/quantity
                    let net_product_count = null;
                    let net_quantity = null;
                    if (
                        periodData.production !== null &&
                        periodData.reject_production !== null
                    ) {
                        net_product_count =
                            periodData.production -
                            periodData.reject_production;
                        if (converted_unit_packing !== null) {
                            net_quantity = roundTo(
                                net_product_count * converted_unit_packing,
                                3
                            );
                        }
                    }

                    // Calculate run rate for the period (products/min)
                    const operating_time_minutes =
                        (periodData.running_duration +
                            periodData.performance_downtime_duration) /
                        60;

                    let run_rate = null;
                    if (operating_time_minutes > 0) {
                        run_rate =
                            periodData.production / operating_time_minutes;
                    }

                    // Calculate running time (minutes) and total operating time (minutes)
                    const running_time_min =
                        periodData.running_duration != null
                            ? periodData.running_duration / 60
                            : 0;

                    const total_operating_time_min = (() => {
                        if (
                            periodData.running_duration == null &&
                            periodData.performance_downtime_duration == null &&
                            periodData.availability_downtime_duration == null
                        ) {
                            return 0;
                        }
                        const running = periodData.running_duration || 0;
                        const perf =
                            periodData.performance_downtime_duration || 0;
                        const avail =
                            periodData.availability_downtime_duration || 0;
                        return (running + perf + avail) / 60;
                    })();

                    const throughput_products_per_min =
                        total_operating_time_min && total_operating_time_min > 0
                            ? (gross_product_count != null
                                  ? gross_product_count
                                  : 0) / total_operating_time_min
                            : 0;

                    const throughput_quantity_per_min =
                        total_operating_time_min && total_operating_time_min > 0
                            ? (gross_quantity != null ? gross_quantity : 0) /
                              total_operating_time_min
                            : 0;

                    const row = [
                        date,
                        this.facilityName,
                        this.getAssetName(asset_id),
                        productName,
                        roundTo(periodData.oee, 5) ?? 0,
                        roundTo(periodData.availability, 5) ?? 0,
                        roundTo(periodData.performance, 5) ?? 0,
                        roundTo(periodData.quality, 5) ?? 0,
                        roundTo(gross_product_count, 3) ?? 0,
                        roundTo(gross_quantity, 3) ?? 0,
                        roundTo(net_product_count, 3) ?? 0,
                        roundTo(net_quantity, 3) ?? 0,
                        roundTo(run_rate, 3) ?? 0,
                        converted_unit_measure,
                        roundTo(throughput_products_per_min, 5) ?? 0,
                        roundTo(throughput_quantity_per_min, 5) ?? 0,
                        roundTo(running_time_min, 5) ?? 0,
                        roundTo(total_operating_time_min, 5) ?? 0
                    ];

                    rows.push(row);
                }
            }
        }

        // Sort the rows by date in ascending order
        rows.sort((a, b) => {
            const dateA = new Date(a[0]);
            const dateB = new Date(b[0]);
            return dateA - dateB;
        });

        // Asset totals row removed as per requirement (only included in period breakdown file)

        return rows;
    }

    /**
     * Get the OEE Metrics rows for an asset
     * @param {Object} assetAggregateData - Aggregated data for the asset
     * @param {String} assetPeriodTotal - Total period for the asset
     * @return {Object} - OEE Metrics row group
     */
    generateTableOEEMetricRow(assetAggregateData, assetPeriodTotal) {
        const oee = { name: i18n.global.t('OEE'), total: null };
        const availability = {
            name: i18n.global.t('Availability'),
            total: null
        };
        const performance = {
            name: i18n.global.t('Performance'),
            total: null
        };
        const quality = { name: i18n.global.t('Quality'), total: null };

        // tally the total oee metrics for each period
        for (const [periodKey, periodData] of Object.entries(
            assetAggregateData
        )) {
            if (periodData !== null) {
                oee[periodKey] = periodData.oee;
                availability[periodKey] = periodData.availability;
                performance[periodKey] = periodData.performance;
                quality[periodKey] = periodData.quality;
            }
        }

        // calculate the overall average (across all products) for each oee metric
        // by getting the sum of all values
        const assetTotal = {
            total_available_time: null,
            total_operating_time: null,
            total_expected_production: null,
            total_production: null,
            total_reject_production: null
        };
        for (const [periodKey, periodData] of Object.entries(
            assetPeriodTotal
        )) {
            assetTotal.total_available_time = addValueToTotal(
                periodData.total_available_time,
                assetTotal.total_available_time
            );
            assetTotal.total_operating_time = addValueToTotal(
                periodData.total_operating_time,
                assetTotal.total_operating_time
            );
            assetTotal.total_expected_production = addValueToTotal(
                periodData.total_expected_production,
                assetTotal.total_expected_production
            );
            assetTotal.total_production = addValueToTotal(
                periodData.total_production,
                assetTotal.total_production
            );
            assetTotal.total_reject_production = addValueToTotal(
                periodData.total_reject_production,
                assetTotal.total_reject_production
            );
        }

        if (
            assetTotal.total_available_time !== null &&
            assetTotal.total_operating_time !== null
        ) {
            availability.total = 1;

            const calculated_availability = calculateAvailability(
                assetTotal.total_operating_time,
                assetTotal.total_available_time
            );
            if (
                !isNaN(calculated_availability) &&
                calculated_availability !== Infinity
            ) {
                availability.total = calculated_availability;
            }
        }

        if (
            assetTotal.total_production !== null &&
            assetTotal.total_expected_production !== null
        ) {
            performance.total = 1;

            const calculated_performance = calculatePerformance(
                assetTotal.total_production,
                assetTotal.total_expected_production
            );
            if (
                !isNaN(calculated_performance) &&
                calculated_performance !== Infinity
            ) {
                performance.total = calculated_performance;
            }
        }

        if (
            assetTotal.total_production !== null &&
            assetTotal.total_reject_production !== null
        ) {
            quality.total = 1;

            const calculated_quality = calculateQuality(
                assetTotal.total_production -
                    assetTotal.total_reject_production,
                assetTotal.total_production
            );
            if (!isNaN(calculated_quality) && calculated_quality !== Infinity) {
                quality.total = calculated_quality;
            }
        }

        if (
            availability.total !== null &&
            performance.total !== null &&
            quality.total !== null
        ) {
            oee.total = availability.total * performance.total * quality.total;
        }

        return {
            group_name: i18n.global.t('OEE Metrics'),
            group_type: 'oee_metrics',
            expand: true,
            details: [oee, availability, performance, quality]
        };
    }

    /**
     * Get the Total Production rows for an asset
     * @param {Object} assetAggregateData - Aggregated data for the asset
     * @return {Object} - Total Production row group
     */
    generateTableTotalProductionRow(assetAggregateData) {
        const gross_product_count = {
            name: i18n.global.t('Total production (products)'),
            total: null
        };
        const net_product_count = {
            name: i18n.global.t('Net production (products)'),
            total: null
        };

        const gross_quantity_by_unit = {};
        const net_quantity_by_unit = {};

        // tally the total production data for each period
        for (const period in assetAggregateData) {
            const periodData = assetAggregateData[period];

            if (periodData !== null) {
                gross_product_count[period] = periodData.gross_product_count;
                if (periodData.gross_product_count !== null) {
                    gross_product_count.total += periodData.gross_product_count;
                }

                // go through each unit in gross_quantity_by_unit and add the total
                for (const unit in periodData.net_quantity_by_unit) {
                    // if there is a net quantity for the unit, initialize the object with the total
                    if (!gross_quantity_by_unit[unit]) {
                        gross_quantity_by_unit[unit] = {
                            name: i18n.global.t('Total quantity ({unit})', {
                                unit: unit
                            }),
                            total: null
                        };
                    }
                    gross_quantity_by_unit[unit][period] =
                        periodData.gross_quantity_by_unit[unit];
                    if (periodData.net_quantity_by_unit[unit] !== null) {
                        gross_quantity_by_unit[unit].total +=
                            periodData.gross_quantity_by_unit[unit];
                    }
                }

                net_product_count[period] = periodData.net_product_count;
                if (periodData.net_product_count !== null) {
                    net_product_count.total += periodData.net_product_count;
                }

                // go through each unit in net_product_count_by_unit and add the total
                for (const unit in periodData.net_quantity_by_unit) {
                    // if there is a net quantity for the unit, initialize the object with the total
                    if (!net_quantity_by_unit[unit]) {
                        net_quantity_by_unit[unit] = {
                            name: i18n.global.t('Net quantity ({unit})', {
                                unit: unit
                            }),
                            total: null
                        };
                    }
                    net_quantity_by_unit[unit][period] =
                        periodData.net_quantity_by_unit[unit];
                    if (periodData.net_quantity_by_unit[unit] !== null) {
                        net_quantity_by_unit[unit].total +=
                            periodData.net_quantity_by_unit[unit];
                    }
                }
            }
        }

        // Get all the rows for each unit
        const gross_quantity_per_unit_rows = Object.values(
            gross_quantity_by_unit
        );
        const net_quantity_per_unit_rows = Object.values(net_quantity_by_unit);

        return {
            group_name: i18n.global.t('Total Production'),
            group_type: 'total_production',
            expand: true,
            details: [
                gross_product_count,
                ...gross_quantity_per_unit_rows,
                net_product_count,
                ...net_quantity_per_unit_rows
            ]
        };
    }

    /**
     * Generate the table row for a product
     * @param {Object} productsData - Raw product data
     * @return {Array} - Array of objects containing the rows
     */
    generateTableProductRows(productsData) {
        const rows = [];

        for (const product_id in productsData) {
            const data = productsData[product_id];
            const productRow = {
                group_name: data.product_name,
                group_type: 'product',
                expand: true,
                details: []
            };

            const gross_product_count = {
                name: i18n.global.t('Total production (products)'),
                total: null
            };
            const gross_quantity = {
                name: i18n.global.t('Total quantity ({unit})', {
                    unit: data.unit_measure
                }),
                total: null
            };
            const net_product_count = {
                name: i18n.global.t('Net production (products)'),
                total: null
            };
            const net_quantity = {
                name: i18n.global.t('Net quantity ({unit})', {
                    unit: data.unit_measure
                }),
                total: null
            };
            const actual_run_rate = {
                name: i18n.global.t('Run Rate (product/min)'),
                total: null
            };

            // clone the product data so that we can delete the product name and unit measure
            const productData = { ...data };
            // delete the product name and unit measure from the data
            // so that only the period data will be left
            // this is important as we iterate through the keys of the object
            // to retrieve the period keys (columns of the table)
            delete productData.product_name;
            delete productData.unit_measure;

            // initialize the total operating time
            // this will be used to calculate the actual run rate for the total
            let total_operating_time = null;

            // for each trend data in the product data, build the table row
            for (const period in productData) {
                const periodData = productData[period];

                if (periodData !== null) {
                    const { production, reject_production, unit_packing } =
                        periodData;

                    gross_product_count[period] = production;
                    if (production !== null) {
                        gross_product_count.total += production;

                        if (unit_packing !== null) {
                            gross_quantity[period] = production * unit_packing;
                            gross_quantity.total += production * unit_packing;
                        }

                        // Calculate the actual run rate for the period
                        // The actual run rate is the production rate per minute
                        const operating_time_minutes =
                            (periodData.running_duration +
                                periodData.performance_downtime_duration) /
                            60;

                        total_operating_time += operating_time_minutes;

                        let run_rate = null;
                        if (operating_time_minutes > 0) {
                            run_rate = production / operating_time_minutes;
                        }
                        actual_run_rate[period] = run_rate;
                    }

                    if (production !== null && reject_production !== null) {
                        const net_production = production - reject_production;
                        net_product_count[period] = net_production;
                        net_product_count.total += net_production;

                        if (unit_packing !== null) {
                            net_quantity[period] =
                                net_production * unit_packing;
                            net_quantity.total += net_production * unit_packing;
                        }
                    }
                }
            }
            // Calculate the actual run rate for the total
            actual_run_rate.total =
                total_operating_time > 0
                    ? gross_product_count.total / total_operating_time
                    : null;

            productRow.details.push(gross_product_count);
            productRow.details.push(gross_quantity);
            productRow.details.push(net_product_count);
            productRow.details.push(net_quantity);
            productRow.details.push(actual_run_rate);

            rows.push(productRow);
        }
        // Sort the rows by date in ascending order
        rows.sort((a, b) => {
            const getDate = row => {
                const key = Object.keys(row.details[0]).find(
                    key => key !== 'name' && key !== 'total'
                );
                return key ? new Date(key) : null;
            };

            const dateA = getDate(a);
            const dateB = getDate(b);

            return dateA - dateB;
        });

        return rows;
    }

    /**
     * Generate the table columns based for an asset
     * @return {Array} - Array of objects containing the columns
     */
    generateTableColumns() {
        const columns = [];
        const periods = this.periods;

        // Add the group name column. Label is null to hide the column label
        columns.push({
            name: 'group_name',
            field: 'name',
            label: null,
            align: 'left'
        });

        // Add the period columns
        periods.forEach(period => {
            columns.push({
                name: period,
                field: period,
                label: getDateLabel(period, this.resolution, this.userLocale),
                align: 'right'
            });
        });

        // Add the total column
        columns.push({
            name: 'total',
            field: 'total',
            label: 'Summary',
            align: 'right'
        });

        return columns;
    }

    /**
     * Initialize trend periods to be used as table columns and chart labels.
     * Given the start and end date, add the missing periods depending on the resolution.
     *
     * @example
     * Start: '2023-01-01T00:00:00',
     * End: '2023-01-04T00:00:00',
     *
     * Add the missing periods depending on the resolution
     * [
     *  '2023-01-01T00:00:00', <-- Start
     *  '2023-01-02T00:00:00',  *Added
     *  '2023-01-03T00:00:00',  *Added
     *  '2023-01-04T00:00:00' <-- End
     * ]
     */
    initReportPeriods() {
        let currentDate = new Date(this.startDate);
        const endDate = new Date(this.endDate);
        const dateGenerator = new DateGenerator(this.resolution);

        while (currentDate < endDate) {
            const currentDateStr = getPeriodKey(currentDate);
            this.periods.push(currentDateStr);

            switch (this.resolution) {
                case 'hourly':
                    currentDate = dateGenerator.hourlyResolution(currentDate);
                    break;
                case '60-minute':
                    currentDate =
                        dateGenerator.sixtyMinuteResolution(currentDate);
                    break;
                case 'daily':
                    currentDate = dateGenerator.dailyResolution(currentDate);
                    break;
                case '24-hour':
                    currentDate =
                        dateGenerator.twentyFourHourResolution(currentDate);
                    break;
                case 'weekly':
                    currentDate = dateGenerator.weeklyResolution(currentDate);
                    break;
                case '7-day':
                    currentDate = dateGenerator.sevenDayResolution(currentDate);
                    break;
                case 'monthly':
                    currentDate = dateGenerator.monthlyResolution(currentDate);
                    break;
            }
        }
    }

    /**
     * Updates the totals for a specific asset with production, OEE metrics and product run rates
     * @param {string|number} asset_id - The unique identifier for the asset
     * @param {Object} totalProductionRow - Object containing production details with total values
     * @param {Object} oeeMetricRow - Object containing OEE metric details with total values
     * @param {Object[]} productRows - Array of product row objects containing run rate details
     */
    updateAssetTotals(asset_id, totalProductionRow, oeeMetricRow, productRows) {
        // Initialize with default values using object spread to avoid nested null checks
        const assetTotal = {
            total_production: 0,
            total_production_quantity: 0,
            total_net_production: 0,
            total_net_production_quantity: 0,
            oee: 0,
            availability: 0,
            performance: 0,
            quality: 0,
            run_rates: [],
            accumulated_running_time_sec: 0,
            accumulated_planned_dt_sec: 0,
            accumulated_unplanned_dt_sec: 0,
            total_operating_time_min: 0,
            running_time_min: 0
        };

        // Extract production totals if available
        if (totalProductionRow?.details) {
            const [gross, quantity, net, netQuantity] =
                totalProductionRow.details;
            assetTotal.total_production = gross?.total ?? 0;
            assetTotal.total_production_quantity = quantity?.total ?? 0;
            assetTotal.total_net_production = net?.total ?? 0;
            assetTotal.total_net_production_quantity = netQuantity?.total ?? 0;
        }

        // Extract OEE metrics if available
        if (oeeMetricRow?.details) {
            const [oee, availability, performance, quality] =
                oeeMetricRow.details;
            assetTotal.oee = oee?.total ?? 0;
            assetTotal.availability = availability?.total ?? 0;
            assetTotal.performance = performance?.total ?? 0;
            assetTotal.quality = quality?.total ?? 0;
        }

        // Collect valid run rates
        assetTotal.run_rates = productRows.reduce((acc, row) => {
            if (row?.details) {
                const rate = row.details[4]?.total;
                if (rate != null) acc.push(rate);
            }
            return acc;
        }, []);

        // Accumulate running time and downtimes using periodTotals to match period row calculations
        if (this.periodTotals[asset_id]) {
            for (const [period, periodTotal] of Object.entries(
                this.periodTotals[asset_id]
            )) {
                // Sum up the total operating time from each period (already in minutes)
                if (periodTotal.total_operating_time != null) {
                    assetTotal.total_operating_time_min +=
                        periodTotal.total_operating_time;
                }
            }
        }

        // Calculate running time using the same method as getFileRowsByPeriod for consistency
        if (this.productsData[asset_id]) {
            const processedPeriods = new Set();

            for (const product of Object.values(this.productsData[asset_id])) {
                for (const [period, periodData] of Object.entries(product)) {
                    if (period === 'product_name' || period === 'unit_measure')
                        continue;
                    if (!periodData || processedPeriods.has(period)) continue;

                    processedPeriods.add(period);

                    // Sum running_duration for this period across all products (same as getFileRowsByPeriod)
                    const runningSumSecForPeriod = Object.values(
                        this.productsData[asset_id]
                    ).reduce((acc, prod) => {
                        const p = prod[period];
                        if (p && p.running_duration != null) {
                            return acc + p.running_duration;
                        }
                        return acc;
                    }, 0);

                    assetTotal.accumulated_running_time_sec +=
                        runningSumSecForPeriod;
                }
            }
        }

        // Calculate running time (min) from accumulated seconds
        assetTotal.running_time_min =
            assetTotal.accumulated_running_time_sec / 60;

        // Calculate throughput (products/min and quantity/min) for asset totals
        // Throughput (products/min) = total number of products / total operating time (min)
        // Throughput (quantity/min) = total quantity / total operating time (min)
        assetTotal.throughput_products_per_min =
            assetTotal.total_operating_time_min > 0
                ? assetTotal.total_production /
                  assetTotal.total_operating_time_min
                : 0;
        assetTotal.throughput_quantity_per_min =
            assetTotal.total_operating_time_min > 0
                ? assetTotal.total_production_quantity /
                  assetTotal.total_operating_time_min
                : 0;

        // Store in assetTotals
        this.assetTotals[asset_id] = assetTotal;
    }

    /**
     * Generates formatted rows of OEE data for a specific asset across different time periods.
     * Each row contains period-specific metrics including OEE, availability, performance, quality,
     * and production counts. A totals row is appended at the end.
     *
     * @param {string|number} asset_id - The identifier of the asset to generate rows for
     * @returns {Array<Array>} An array of rows where each row contains:
     *   - Period (formatted according to user locale)
     *   - Facility name
     *   - Asset name
     *   - Product category (always "All Products")
     *   - OEE value (rounded to 5 decimals)
     *   - Availability value (rounded to 5 decimals)
     *   - Performance value (rounded to 5 decimals)
     *   - Quality value (rounded to 5 decimals)
     *   - Gross product count (rounded to 3 decimals)
     *   - Gross quantity (rounded to 3 decimals)
     *   - Net product count (rounded to 3 decimals)
     *   - Net quantity (rounded to 3 decimals)
     *   - Throughput (products/min) (rounded to 5 decimals)
     *   - Throughput (quantity/min) (rounded to 5 decimals)
     *   - Running time (min) (rounded to 3 decimals)
     *   - Total operating time (min) (rounded to 3 decimals)
     */
    getFileRowsByPeriod(asset_id) {
        const rows = [];
        for (const period in this.aggregatedData[asset_id]) {
            const periodData = this.aggregatedData[asset_id][period];

            if (
                periodData?.oee === null &&
                periodData?.availability === null &&
                periodData?.performance === null &&
                periodData?.quality === null &&
                periodData?.gross_product_count === null &&
                periodData?.gross_quantity === null &&
                periodData?.net_product_count === null &&
                periodData?.net_quantity === null
            ) {
                continue;
            }

            // attempt to build running and operating time metrics
            const periodTotals =
                this.periodTotals[asset_id] &&
                this.periodTotals[asset_id][period];

            // running_time_min: sum running_duration from productsData if available
            let running_time_min = null;
            if (this.productsData[asset_id]) {
                const runningSumSec = Object.values(
                    this.productsData[asset_id]
                ).reduce((acc, prod) => {
                    const p = prod[period];
                    if (p && p.running_duration != null)
                        return acc + p.running_duration;
                    return acc;
                }, 0);
                running_time_min =
                    runningSumSec > 0 ? runningSumSec / 60 : null;
            }

            // total operating time (min) from periodTotals if present
            let total_operating_time_min = null;
            if (periodTotals && periodTotals.total_operating_time != null) {
                total_operating_time_min = periodTotals.total_operating_time;
            }

            const gross_product_count = periodData?.gross_product_count ?? null;
            const gross_quantity = periodData?.gross_quantity ?? null;

            const throughput_products_per_min =
                total_operating_time_min && total_operating_time_min > 0
                    ? (gross_product_count != null ? gross_product_count : 0) /
                      total_operating_time_min
                    : null;

            const throughput_quantity_per_min =
                total_operating_time_min && total_operating_time_min > 0
                    ? (gross_quantity != null ? gross_quantity : 0) /
                      total_operating_time_min
                    : null;

            rows.push([
                getLocaleFormat(period, this.userLocale),
                this.facilityName,
                this.assetIdToName[asset_id],
                i18n.global.t('All Products'),
                roundTo(periodData?.oee, 5) ?? 0,
                roundTo(periodData?.availability, 5) ?? 0,
                roundTo(periodData?.performance, 5) ?? 0,
                roundTo(periodData?.quality, 5) ?? 0,
                roundTo(periodData?.gross_product_count, 3) ?? 0,
                roundTo(periodData?.gross_quantity, 3) ?? 0,
                roundTo(periodData?.net_product_count, 3) ?? 0,
                roundTo(periodData?.net_quantity, 3) ?? 0,
                // new columns appended in order:
                throughput_products_per_min != null
                    ? roundTo(throughput_products_per_min, 5)
                    : 0,
                throughput_quantity_per_min != null
                    ? roundTo(throughput_quantity_per_min, 5)
                    : 0,
                running_time_min != null ? roundTo(running_time_min, 3) : 0,
                total_operating_time_min != null
                    ? roundTo(total_operating_time_min, 3)
                    : 0
            ]);
        }

        // Add the totals row, matching the period row structure (including throughput and time columns)
        const assetTotals = this.assetTotals[asset_id] || {};
        const assetName = this.getAssetName(asset_id);
        rows.push([
            getLocaleFormat(this.endDate, this.userLocale),
            this.facilityName,
            i18n.global.t('Total for {assetName}', { assetName: assetName }),
            i18n.global.t('All Products'),
            roundTo(assetTotals?.oee, 5) ?? 0,
            roundTo(assetTotals?.availability, 5) ?? 0,
            roundTo(assetTotals?.performance, 5) ?? 0,
            roundTo(assetTotals?.quality, 5) ?? 0,
            roundTo(assetTotals?.total_production, 3) ?? 0,
            roundTo(assetTotals?.total_production_quantity, 3) ?? 0,
            roundTo(assetTotals?.total_net_production, 3) ?? 0,
            roundTo(assetTotals?.total_net_production_quantity, 3) ?? 0,
            assetTotals?.throughput_products_per_min != null
                ? roundTo(assetTotals.throughput_products_per_min, 5)
                : 0,
            assetTotals?.throughput_quantity_per_min != null
                ? roundTo(assetTotals.throughput_quantity_per_min, 5)
                : 0,
            assetTotals?.running_time_min != null
                ? roundTo(assetTotals.running_time_min, 3)
                : 0,
            assetTotals?.total_operating_time_min != null
                ? roundTo(assetTotals.total_operating_time_min, 3)
                : 0
        ]);

        return rows;
    }

    /**
     * Retrieves the name of an asset based on its ID.
     * If the asset is deleted, appends "(Deleted)" to the asset name.
     * @param {number} asset_id - The ID of the asset.
     * @returns {string} The name of the asset, with "(Deleted)" appended if the asset is deleted.
     */
    getAssetName(asset_id) {
        let assetName = this.assetIdToName[asset_id];
        // If the asset is deleted, append (Deleted) to the asset name
        const asset = getAsset(asset_id);
        const isDeleted = asset ? asset.is_deleted || false : false;
        if (isDeleted) {
            assetName = i18n.global.t('{assetName} (Deleted)', {
                assetName: assetName
            });
        }
        return assetName;
    }
}
